/**
 * File: useContractsMutations.ts
 * Path: apps/admin/src/shared/hooks/useContractsMutations.ts
 * Description: Mutation hooks split out of useContracts.ts.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ACCOUNTING_COLLECTIONS,
  type Allocation,
  type ContractLinkPurpose,
  type ContractSettings,
} from "@idk/config";
import { receiptMessage } from "../utils/receiptMessageUtils";
import { toast } from "@idk/ui";
import { clientsKey } from "@/shared/hooks/useClientsQueries";
import {
  contractsRepo,
  type DraftPatch,
  type ImportSignedInput,
  type NewContractInput,
} from "../services/contractsService";
import { contractsKey, eventsKey, remindersKey, settingsKey } from "./useContractsQueries";

/** A callable's message is written for people; show it as it is. */
export const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;
const reportError = (action: string) => (error: unknown) => {
  console.error(`[Contracts] ${action} failed:`, error);
  toast.error(errorMessage(error, `Could not ${action}.`));
};
export const useContractMutations = () => {
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: contractsKey });

  return {
    /** Logs that a WhatsApp reminder went out; a failure is silent, the message itself is already open. */
    logReminder: useMutation({
      mutationFn: (input: { contractId: string; reference: string; sentOn: string }) =>
        contractsRepo.logPaymentReminder(input.contractId, input.reference, input.sentOn),
      onSuccess: (_data, input) =>
        void queryClient.invalidateQueries({ queryKey: remindersKey(input.contractId) }),
    }),

    markReceived: useMutation({
      mutationFn: (input: {
        contractId: string;
        reference: string;
        amount: number;
        receivedOn: string;
        split?: { allocations: Allocation[]; creditCents: number };
      }) =>
        contractsRepo.markReceived(input.contractId, {
          reference: input.reference,
          amount: input.amount,
          receivedOn: input.receivedOn,
          ...(input.split ? { split: input.split } : {}),
        }),
      onSuccess: (result) => {
        refresh();
        // The books moved with it, and so did the invoices.
        void queryClient.invalidateQueries({ queryKey: [ACCOUNTING_COLLECTIONS.profiles] });
        const said = receiptMessage(result);
        if (said) (result.booked ? toast.success : toast.warning)(said);
      },
      onError: reportError("record the payment"),
    }),

    setDueDate: useMutation({
      mutationFn: (input: { contractId: string; reference: string; dueOn: string }) =>
        contractsRepo.setDueDate(input.contractId, input.reference, input.dueOn),
      onSuccess: refresh,
      onError: reportError("save the due date"),
    }),

    clearDueDate: useMutation({
      mutationFn: (input: { contractId: string; reference: string }) =>
        contractsRepo.clearDueDate(input.contractId, input.reference),
      onSuccess: refresh,
      onError: reportError("clear the due date"),
    }),

    undoReceived: useMutation({
      mutationFn: (input: { contractId: string; reference: string }) =>
        contractsRepo.undoReceived(input.contractId, input.reference),
      onSuccess: () => {
        refresh();
        void queryClient.invalidateQueries({ queryKey: [ACCOUNTING_COLLECTIONS.profiles] });
      },
      onError: reportError("undo the payment"),
    }),

    create: useMutation({
      mutationFn: (input: NewContractInput) => contractsRepo.create(input),
      onSuccess: () => {
        refresh();
        // The client's contract count moved on.
        void queryClient.invalidateQueries({ queryKey: clientsKey });
      },
      onError: reportError("create the contract"),
    }),

    importSigned: useMutation({
      mutationFn: (input: ImportSignedInput) => contractsRepo.importSigned(input),
      onSuccess: () => {
        refresh();
        // The client's contract count moved on, and a retainer may have appeared.
        void queryClient.invalidateQueries({ queryKey: clientsKey });
      },
      onError: reportError("add the contract"),
    }),

    assignReference: useMutation({
      mutationFn: (input: { id: string; clientId: string }) =>
        contractsRepo.assignReference(input.id, input.clientId),
      onSuccess: () => {
        refresh();
        void queryClient.invalidateQueries({ queryKey: clientsKey });
      },
      onError: reportError("give the contract its reference"),
    }),

    updateDraft: useMutation({
      mutationFn: (input: { id: string; patch: DraftPatch }) =>
        contractsRepo.updateDraft(input.id, input.patch),
      onSuccess: refresh,
      onError: reportError("save the draft"),
    }),

    updateNotes: useMutation({
      mutationFn: (input: { id: string; notes: string }) =>
        contractsRepo.updateNotes(input.id, input.notes),
      onSuccess: refresh,
      onError: reportError("save the notes"),
    }),

    withdraw: useMutation({
      mutationFn: (id: string) => contractsRepo.withdraw(id),
      onSuccess: refresh,
      onError: reportError("withdraw the contract"),
    }),

    issueLink: useMutation({
      mutationFn: (input: { id: string; purpose: ContractLinkPurpose }) =>
        contractsRepo.issueLink(input.id, input.purpose),
      onSuccess: refresh,
      onError: reportError("create the link"),
    }),

    pdf: useMutation({
      mutationFn: (id: string) => contractsRepo.pdf(id),
      // A stored PDF's download goes on the audit trail.
      onSuccess: (_pdf, id) =>
        void queryClient.invalidateQueries({ queryKey: eventsKey(id) }),
      onError: reportError("get the PDF"),
    }),

    countersign: useMutation({
      mutationFn: (input: { id: string; signature: string }) =>
        contractsRepo.countersign(input.id, input.signature),
      onSuccess: refresh,
      onError: reportError("countersign"),
    }),

    saveSettings: useMutation({
      mutationFn: (settings: ContractSettings) =>
        contractsRepo.saveSettings(settings),
      onSuccess: () =>
        void queryClient.invalidateQueries({ queryKey: settingsKey }),
      onError: reportError("save the payment details"),
    }),
  };
};
